import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { useSubscriptionNavigation } from '@Src/hooks/useSubscriptionNavigation';
import { NoSubscriptionDialog } from '@Components/NoSubscriptionDialog/NoSubscriptionDialog';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@Src/app/hooks';
import { AppRouteURL, AppRouteURLwithChilds, TestSereisRouteURL } from '@Constants/app-route-url.constant';
import { isLoggedIn } from '@Src/utils/service/app.utility.service';
import SvgIcon from '@Components/icons/SvgIcon';

export interface VideoCardProps {
    videoId: string | number;
    videoTitle: string;
    description: string;
    videoLink: string;
    fullVideoLink?: string; // Optional link for full video
    video: any;
    className?: string;
    height?: number | string; // Total card height (default: 450)
    maxWidth?: number | string; // Max width of the card (default: 100%)
    playInPlace?: boolean; // If true, plays video in the card instead of a modal
    isCurrentPlaying?: boolean; // For tracking global playback state
    onPlay?: () => void; // Callback when video starts playing
}

/**
 * Reusable VideoCard component with consistent styling and CSS-based truncation
 */
export const VideoCard: React.FC<VideoCardProps> = ({
    videoId,
    videoTitle,
    description,
    videoLink,
    fullVideoLink,
    video,
    className = '',
    height = 450,
    maxWidth = '100%',
    playInPlace = false,
    isCurrentPlaying = false,
    onPlay,
}) => {
    const [visible, setVisible] = useState(false);
    const [showFullVideo, setShowFullVideo] = useState(false);
    const navigate = useNavigate();
    const coursePreferences = useAppSelector((state: any) => state.coursePreferences);
    const boardId = coursePreferences?.boardDetails?.[0]?.Id;
    const boardName = coursePreferences?.boardDetails?.[0]?.Name;
    const classId = coursePreferences?.classDetails?.[0]?.Id;
    const ClassName = coursePreferences?.classDetails?.[0]?.Name;

    // Extract YouTube ID for thumbnail
   const getYouTubeId = (url: string) => {
    if (!url) return null;

    const regExp = /^.*(youtu.be\/|shorts\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    return (match && match[2].length === 11) ? match[2] : null;
};
    const youtubeId = getYouTubeId(videoLink);
    const thumbnailUrl = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg` : null;

    // Extract YouTube video ID from URL
 const getYouTubeEmbedUrl = (url: string | undefined) => {
    if (!url) return null;

    const regExp = /^.*(youtu.be\/|shorts\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}`;
    }

    if (url.includes('youtube.com/embed/')) {
        return url;
    }

    return null;
};

    const embedUrl = getYouTubeEmbedUrl(videoLink);
    const fullEmbedUrl = getYouTubeEmbedUrl(fullVideoLink);

    const getAutoplayUrl = (url: string | null) => {
        if (!url) return '';
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}autoplay=1`;
    };

    const { checkSubscriptionAndNavigate, subscriptionDialogProps } = useSubscriptionNavigation();
    const [hasInteracted, setHasInteracted] = useState(false);

    // Reset interaction state when global playback stops for this video
    useEffect(() => {
        if (!isCurrentPlaying) {
            setHasInteracted(false);
        }
    }, [isCurrentPlaying]);

    const handleCardClick = (e: React.MouseEvent) => {
        // Prevent event bubbling if necessary
        if (playInPlace) {
            setHasInteracted(true); // Mark this specific instance as the one to play
            if (onPlay) onPlay();
        } else {
            setVisible(true);
        }
    };

    // Calculate height for the text section
    const cardHeight = typeof height === 'number' ? `${height}px` : height;
    const cardMaxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    // Dynamic thumbnail and text section scaling
    const isSmallCard = typeof height === 'number' && height < 400;
    const thumbHeightValue = isSmallCard ? 160 : 256;
    const thumbHeightClass = isSmallCard ? 'tw-h-40' : 'tw-h-64';
    const textSectionHeight = typeof height === 'number' ? `${height - thumbHeightValue}px` : 'auto';

    return (
        <div className={`tw-mb-3 tw-mx-auto ${className}`} style={{ maxWidth: cardMaxWidth }}>
            <div className='tw-flex flex-column tw-justify-center tw-align-center'>
                <div
                    className="tw-rounded-2xl tw-overflow-hidden tw-shadow-lg tw-flex tw-flex-col hover:tw-shadow-xl tw-transition-all tw-duration-300 tw-bg-white tw-border tw-border-gray-100"
                    style={{ height: cardHeight }}
                >
                    {embedUrl ? (
                        <div className={`tw-relative tw-w-full tw-cursor-pointer  ${thumbHeightClass} tw-bg-black tw-flex-shrink-0`} onClick={handleCardClick}>
                            {playInPlace && isCurrentPlaying && hasInteracted ? (
                                <iframe
                                    className="tw-w-full tw-h-full tw-object-cover"
                                    src={getAutoplayUrl(embedUrl)}
                                    title={videoTitle}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : (
                                <div className="tw-relative tw-w-full tw-h-full">
                                    <img
                                        src={thumbnailUrl || '/assets/images/video-placeholder.png'}
                                        alt={videoTitle}
                                        className="tw-w-full tw-h-full tw-object-cover"
                                        onError={(e) => {
                                            // Fallback to mqdefault if maxres doesn't exist
                                            const target = e.target as HTMLImageElement;
                                            if (youtubeId && !target.src.includes('mqdefault')) {
                                                target.src = `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
                                            }
                                        }}
                                    />
                                    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/20 hover:tw-bg-black/10 tw-transition-colors">
                                        <div className={`tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-transition-transform ${playInPlace ? 'hover:tw-scale-110' : ''}`} style={{ backgroundColor: '#7f56d9' }}>
                                            <div className="tw-ml-1 tw-w-0 tw-h-0 tw-border-y-[10px] tw-border-y-transparent tw-border-l-[18px] tw-border-l-white" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={`tw-w-full ${thumbHeightClass} tw-bg-gray-200 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                            <p className="tw-text-gray-500">Video unavailable</p>
                        </div>
                    )}

                    <div
                        className="tw-p-5 tw-flex tw-flex-col tw-flex-grow tw-justify-start"
                        style={{ height: textSectionHeight }}
                    >
                        <div
                            className="tw-font-bold tw-text-lg tw-text-gray-900 tw-line-clamp-2 tw-leading-snug tw-mb-2"
                            title={videoTitle}
                            style={{ minHeight: '3rem' }}
                        >
                            {videoTitle}
                        </div>
                        <p
                            className="tw-text-gray-600 tw-text-sm tw-line-clamp-2 tw-leading-relaxed tw-mt-1"
                            title={description}
                            style={{ minHeight: '3rem' }}
                        >
                            {description}
                        </p>
                    </div>
                    <div className='tw-text-center'>

                        <button className='ia-btn ia-btn-primary py-2 px-3' style={{ position: "relative",bottom:"15px", width: "90%" }}
                            onClick={() => {
                                isLoggedIn() ?
                                    navigate(`${TestSereisRouteURL.TEST_SERIES}`, {
                                        state: {
                                            boardId: boardId,
                                            boardName: boardName,
                                            classId: classId,
                                            className: ClassName,
                                            subjectId: video.SubjectId,
                                            subjectName: video.subjectName,
                                            chapterId: video.ChapterId,
                                            chapterName: video.chapterName,
                                            topicId: video.TopicId,
                                            topicName: video.topicName,

                                        }
                                    }) : navigate(AppRouteURL.AUTH + AppRouteURLwithChilds.AUTH.childrens.LOGIN.path)
                            }}
                        >
                            Start Test <SvgIcon name="rightArrow" size={16} />
                        </button>
                    </div>

                </div>



            </div>


            {!playInPlace && (
                <Dialog
                    header={videoTitle}
                    visible={visible}
                    style={{ width: '70vw', maxHeight: '90vh' }}
                    breakpoints={{ '960px': '85vw', '640px': '95vw' }}
                    onHide={() => {
                        setVisible(false);
                        setShowFullVideo(false);
                    }}
                    dismissableMask={true}
                    modal={true}
                    blockScroll={true}
                    className="custom-video-dialog"
                    footer={
                        (fullEmbedUrl && !showFullVideo) ? (
                            <div className="tw-flex tw-justify-end tw-py-2">
                                <button
                                    onClick={() => checkSubscriptionAndNavigate(null, () => setShowFullVideo(true))}
                                    className="tw-px-3 tw-py-1.5 md:tw-px-4 md:tw-py-2 tw-text-sm md:tw-text-base tw-rounded-lg tw-text-white tw-bg-[#7f56d9] tw-hover:opacity-90 tw-transition-all tw-font-semibold"
                                    style={{ backgroundColor: '#7f56d9' }}
                                >
                                    Display full video
                                </button>
                            </div>
                        ) : null
                    }
                >
                    {visible && (embedUrl || fullEmbedUrl) && (
                        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-gap-5 tw-p-2 md:tw-p-4">
                            <div className="tw-w-full tw-aspect-video tw-rounded-xl tw-overflow-hidden tw-shadow-2xl">
                                <iframe
                                    className="tw-w-full tw-h-full"
                                    style={{ aspectRatio: '16/9' }}
                                    src={getAutoplayUrl(showFullVideo && fullEmbedUrl ? fullEmbedUrl : embedUrl)}
                                    title={videoTitle}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>

                            <div className="tw-py-1 md:tw-py-2">
                                <div className="tw-flex tw-justify-between tw-items-center tw-mb-2 md:tw-mb-3">
                                    <h4 className="tw-font-bold tw-text-lg md:tw-text-xl tw-text-gray-900 tw-m-0">Description</h4>
                                </div>
                                <div className="tw-h-[1px] tw-w-full tw-bg-gray-100 tw-mb-3 md:tw-mb-4" />
                                <p className="tw-text-gray-700 tw-text-xs md:tw-text-base tw-whitespace-pre-wrap tw-leading-relaxed">
                                    {description}
                                </p>
                            </div>
                        </div>
                    )}
                </Dialog>
            )}
            <NoSubscriptionDialog {...subscriptionDialogProps} />
        </div>
    );
};
